export const formatKeyToLabel = (key) => {
  if (!key) {
    return "";
  }

  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[a-z]/, (char) => char.toUpperCase());
};

const stringifyValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "bigint") {
    return String(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => stringifyValue(item)).join(", ");
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  return String(value);
};

export const formatValueForTable = (value) => {
  const stringValue = stringifyValue(value);
  return stringValue === "" ? "—" : stringValue;
};

export const formatValueForDetails = (value) => {
  if (value === null || value === undefined) {
    return "Not provided";
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return formatValueForTable(value);
    }
  }

  if (Array.isArray(value)) {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return value.map((item) => formatValueForTable(item)).join(", ");
    }
  }

  return formatValueForTable(value);
};

const nestedValue = (payload, path) => {
  return path
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), payload);
};

export const normalizeApiListResponse = (payload) => {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }

  if (!payload || typeof payload !== "object") {
    return { items: [], total: 0 };
  }

  const listCandidatePaths = [
    "items",
    "data",
    "results",
    "rows",
    "records",
    "entries",
    "values",
    "payload",
    "response",
    "body",
    "enrollments",
  ];

  let items = null;

  for (const path of listCandidatePaths) {
    const candidate = nestedValue(payload, path);
    if (Array.isArray(candidate)) {
      items = candidate;
      break;
    }
  }

  if (!items && Array.isArray(payload?.data?.items)) {
    items = payload.data.items;
  }

  if (!items) {
    items = [];
  }

  const totalCandidatePaths = [
    "total",
    "count",
    "totalCount",
    "meta.total",
    "meta.count",
    "meta.totalCount",
    "pagination.total",
    "pagination.count",
    "pagination.totalCount",
    "page.total",
    "page.count",
    "page.totalCount",
  ];

  let total = null;

  for (const path of totalCandidatePaths) {
    const candidate = nestedValue(payload, path);
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      total = candidate;
      break;
    }
  }

  if (total === null) {
    total = items.length;
  }

  const pageCandidatePaths = [
    "page",
    "currentPage",
    "meta.page",
    "pagination.page",
    "pagination.current",
    "pageIndex",
  ];

  let page = null;

  for (const path of pageCandidatePaths) {
    const candidate = nestedValue(payload, path);
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      page = candidate;
      break;
    }
  }

  const pageSizeCandidatePaths = [
    "pageSize",
    "limit",
    "perPage",
    "meta.pageSize",
    "pagination.pageSize",
    "pagination.limit",
    "page.limit",
  ];

  let pageSize = null;

  for (const path of pageSizeCandidatePaths) {
    const candidate = nestedValue(payload, path);
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      pageSize = candidate;
      break;
    }
  }

  const nextTokenCandidatePaths = [
    "nextToken",
    "pagination.nextToken",
    "page.nextToken",
    "meta.nextToken",
    "links.next",
    "next",
  ];

  let nextToken = null;

  for (const path of nextTokenCandidatePaths) {
    const candidate = nestedValue(payload, path);
    if (candidate === undefined || candidate === null) {
      continue;
    }

    if (typeof candidate === "string") {
      if (candidate.trim() !== "") {
        nextToken = candidate.trim();
        break;
      }
      continue;
    }

    if (candidate !== "") {
      nextToken = candidate;
      break;
    }
  }

  const hasMoreCandidatePaths = [
    "hasMore",
    "hasNext",
    "pagination.hasMore",
    "pagination.hasNext",
    "meta.hasMore",
    "meta.hasNext",
    "page.hasMore",
  ];

  let hasMore = null;

  for (const path of hasMoreCandidatePaths) {
    const candidate = nestedValue(payload, path);
    if (typeof candidate === "boolean") {
      hasMore = candidate;
      break;
    }
    if (typeof candidate === "string") {
      const normalized = candidate.trim().toLowerCase();
      if (normalized === "true" || normalized === "false") {
        hasMore = normalized === "true";
        break;
      }
    }
  }

  return {
    items,
    total,
    page,
    pageSize,
    nextToken,
    hasMore,
  };
};

export const sanitizeRowsForDisplay = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return row;
    }

    const { GPK1, GSK1, Guid, ...rest } = row;

    const sanitizedRow = {};

    if (Guid !== undefined) {
      sanitizedRow.id = Guid;
    }

    Object.keys(rest).forEach((key) => {
      sanitizedRow[key] = rest[key];
    });

    return sanitizedRow;
  });
};

export const extractColumnsFromRows = (rows) => {
  const columnSet = new Set();

  rows.forEach((row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.keys(row).forEach((key) => {
        columnSet.add(key);
      });
    }
  });

  return Array.from(columnSet);
};
